import React from "react";

import { Info, Save } from "lucide-react";

import { useOutletContext } from "react-router-dom";

import { useTranslation } from "react-i18next";

import {
  EUROPEAN_COUNTRIES,
  PROPERTY_PURCHASE_COUNTRIES,
} from "../../../../data/countries";

import { useAuth0 } from "@auth0/auth0-react";

import {
  fetchMarketRegions,
  initializeDossierFromPersonalInfo,
  updateDossierPersonalInfo,
} from "./homebuyerDossierApi";

import "./HomebuyerPersonalInfo.css";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

function loadGoogleMaps(apiKey) {
  if (!apiKey) {
    return Promise.resolve(null);
  }

  if (window.google?.maps?.importLibrary) {
    return Promise.resolve(window.google);
  }

  if (window.__legupGoogleMapsPromise) {
    return window.__legupGoogleMapsPromise;
  }

  window.__legupGoogleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("legup-google-maps-script");

    if (existing) {
      if (window.google?.maps?.importLibrary) {
        resolve(window.google);
        return;
      }

      existing.addEventListener("error", reject, { once: true });

      return;
    }

    const callbackName = "__legupGoogleMapsLoaded";

    window[callbackName] = () => {
      try {
        resolve(window.google);
      } finally {
        delete window[callbackName];
      }
    };

    const script = document.createElement("script");

    script.id = "legup-google-maps-script";
    script.async = true;

    const params = new URLSearchParams({
      key: apiKey,
      loading: "async",
      callback: callbackName,
      v: "weekly",
    });

    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;

    script.onerror = (error) => {
      delete window[callbackName];
      window.__legupGoogleMapsPromise = null;
      reject(error);
    };

    document.head.appendChild(script);
  });

  return window.__legupGoogleMapsPromise;
}

const EMPTY_PERSONAL_INFO = {
  first_name: "",

  last_name: "",

  date_of_birth: "",

  nationality: "",

  country_of_residence: "",

  residency_status: "",

  residency_status_other: "",

  national_id_number: "",

  address: "",

  email: "",

  phone: "",

  marital_status: "",

  dependents: "",

  preferred_contact_method: "EMAIL",

  notes: "",
};

const REQUIRED_PRIMARY_FIELDS = [
  "first_name",

  "last_name",

  "date_of_birth",

  "nationality",

  "country_of_residence",

  "residency_status",

  "address",

  "email",

  "phone",

  "marital_status",

  "dependents",

  "preferred_contact_method",
];

const REQUIRED_CO_BORROWER_FIELDS = [
  "first_name",

  "last_name",

  "date_of_birth",

  "email",
];

function createBorrower(role = "PRIMARY") {
  return {
    id: role === "PRIMARY" ? "primary" : "co_applicant",

    role,

    personal_info: { ...EMPTY_PERSONAL_INFO },

    financial_info: {},

    documents: {},

    extracted_data: {},
  };
}

function getBorrowersFromDraft(dossierDraft, account, profile) {
  const existingBorrowers = dossierDraft?.borrowers;

  if (Array.isArray(existingBorrowers) && existingBorrowers.length > 0) {
    return normalizeBorrowersForForm(existingBorrowers);
  }

  const primary = createBorrower("PRIMARY");

  primary.personal_info = normalizePersonalInfoForForm({
    ...primary.personal_info,

    first_name: account?.first_name || "",

    last_name: account?.last_name || "",

    email: account?.email || "",

    country_of_residence: profile?.country_of_residence || "",

    national_id_number: profile?.national_id_number || "",

    phone: profile?.phone || "",

    marital_status: account?.family_situation || "",
  });

  return [primary];
}

function formatDateInput(value = "") {
  const digits = value.replace(/\D/g, "").slice(0, 8);

  if (digits.length <= 2) return digits;

  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
}

function isValidEmail(email = "") {
  const normalized = String(email || "").trim();

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
}

function isValidDateString(value = "") {
  const normalized = String(value || "").trim();

  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) return false;

  const [dayStr, monthStr, yearStr] = normalized.split("/");

  const day = Number(dayStr);

  const month = Number(monthStr);

  const year = Number(yearStr);

  if (year < 1900 || year > 2100) return false;

  if (month < 1 || month > 12) return false;

  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function hasEnoughPhoneDigits(phone = "") {
  const digits = String(phone || "").replace(/\D/g, "");

  return digits.length >= 6;
}

function isRequiredField(fieldName, mode = "primary") {
  if (mode === "coBorrower") {
    return REQUIRED_CO_BORROWER_FIELDS.includes(fieldName);
  }

  return REQUIRED_PRIMARY_FIELDS.includes(fieldName);
}

function normalizePersonalInfoForForm(personal = {}) {
  return {
    ...EMPTY_PERSONAL_INFO,

    ...personal,

    date_of_birth: personal.date_of_birth?.includes("-")
      ? personal.date_of_birth.split("-").reverse().join("/")
      : personal.date_of_birth || "",

    dependents:
      personal.dependents === null || personal.dependents === undefined
        ? ""
        : String(personal.dependents),
  };
}

function normalizeBorrowersForForm(borrowers = []) {
  return borrowers.map((borrower) => ({
    ...borrower,

    personal_info: normalizePersonalInfoForForm(borrower.personal_info),
  }));
}

export default function HomebuyerPersonalInfo() {
  const { t, i18n } = useTranslation("homebuyer-personal-info");

  const { getAccessTokenSilently } = useAuth0();

  const {
    currentStepKey,

    handleStepValidityChange,

    dossierDraft,

    openSaveModal,

    updateDraftMetaFields,

    registerStepSubmitHandler,

    setBorrowersDraft,

    dossierId,

    account,

    profile,
  } = useOutletContext();

  const activeDraft = dossierDraft || {};

  const activeDossierId = dossierId || activeDraft?.dossier_id || null;

  const [borrowers, setBorrowers] = React.useState(() =>
    getBorrowersFromDraft(activeDraft, account, profile),
  );
  // Dossier market
  const [propertyCountry, setPropertyCountry] = React.useState(
    activeDraft?.country_of_property_purchase ||
      activeDraft?.market_region?.country_code ||
      profile?.country_of_property_purchase ||
      "",
  );

  const [marketRegionId, setMarketRegionId] = React.useState(
    activeDraft?.market_region_id
      ? String(activeDraft.market_region_id)
      : activeDraft?.market_region?.id
        ? String(activeDraft.market_region.id)
        : "",
  );

  const [marketRegions, setMarketRegions] = React.useState([]);

  const [loadingMarketRegions, setLoadingMarketRegions] = React.useState(false);

  const draftBorrowersKey = React.useMemo(
    () => JSON.stringify(activeDraft?.borrowers || []),

    [activeDraft?.borrowers],
  );

  const [prefilledFields, setPrefilledFields] = React.useState({});

  const [fieldErrors, setFieldErrors] = React.useState({});

  const [submitError, setSubmitError] = React.useState("");

  const [addressSuggestions, setAddressSuggestions] = React.useState([]);
  const [addressSuggestionsOpen, setAddressSuggestionsOpen] =
    React.useState(false);
  const [addressSearching, setAddressSearching] = React.useState(false);

  const addressSearchTimerRef = React.useRef(null);
  const addressRequestIdRef = React.useRef(0);
  const addressSessionTokenRef = React.useRef(null);
  const addressAutocompleteRef = React.useRef(null);

  // Prefill profile fields when they arrive asynchronously; never replace
  // already-entered dossier or form data.

  const FIELD_TOOLTIPS = React.useMemo(
    () => ({
      first_name: t("tooltips.first_name"),

      last_name: t("tooltips.last_name"),

      date_of_birth: t("tooltips.date_of_birth"),

      nationality: t("tooltips.nationality"),

      country_of_residence: t("tooltips.country_of_residence"),

      residency_status: t("tooltips.residency_status"),

      national_id_number: t("tooltips.national_id_number"),

      address: t("tooltips.address"),

      email: t("tooltips.email"),

      phone: t("tooltips.phone"),

      marital_status: t("tooltips.marital_status"),

      dependents: t("tooltips.dependents"),

      preferred_contact_method: t("tooltips.preferred_contact_method"),

      notes: t("tooltips.notes"),
    }),

    [t],
  );

  const primaryBorrower = borrowers[0] || createBorrower("PRIMARY");

  const coBorrower = borrowers[1] || null;

  const primaryInfo = primaryBorrower.personal_info || EMPTY_PERSONAL_INFO;

  const coBorrowerInfo = coBorrower?.personal_info || EMPTY_PERSONAL_INFO;

  const hasCoBorrower =
    primaryInfo.marital_status &&
    !["SINGLE", "DIVORCED", "WIDOWED"].includes(primaryInfo.marital_status);

  const selectedMarketRegion = React.useMemo(
    () =>
      marketRegions.find(
        (region) => String(region.id) === String(marketRegionId),
      ) ||
      activeDraft?.market_region ||
      null,

    [marketRegions, marketRegionId, activeDraft?.market_region],
  );
  // Load active LEG-UP markets for the selected country.
  React.useEffect(() => {
    let cancelled = false;

    async function loadMarketRegions() {
      if (!propertyCountry) {
        setMarketRegions([]);

        if (!activeDossierId) {
          setMarketRegionId("");
        }

        return;
      }

      try {
        setLoadingMarketRegions(true);

        const accessToken = await getAccessTokenSilently();

        const regions = await fetchMarketRegions({
          accessToken,

          countryCode: propertyCountry,
        });

        if (cancelled) return;

        setMarketRegions(regions);

        if (!activeDossierId) {
          setMarketRegionId((current) => {
            const currentStillValid = regions.some(
              (region) => String(region.id) === String(current),
            );

            if (currentStillValid) {
              return current;
            }

            if (regions.length === 1) {
              return String(regions[0].id);
            }

            return "";
          });
        }
      } catch (error) {
        if (!cancelled) {
          setSubmitError(
            error?.message ||
              t(
                "market.errors.load",

                "Could not load the available LEG-UP markets.",
              ),
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingMarketRegions(false);
        }
      }
    }

    loadMarketRegions();

    return () => {
      cancelled = true;
    };
  }, [propertyCountry, activeDossierId, getAccessTokenSilently, t]);

  React.useEffect(() => {
    const draftPrimary =
      activeDraft?.borrowers?.find((b) => b?.role === "PRIMARY")
        ?.personal_info || {};
    const mappedProfile = {
      first_name: draftPrimary.first_name || account?.first_name || "",
      last_name: draftPrimary.last_name || account?.last_name || "",
      email: draftPrimary.email || account?.email || "",
      date_of_birth:
        normalizePersonalInfoForForm(draftPrimary).date_of_birth || "",
      country_of_residence:
        draftPrimary.country_of_residence || profile?.country_of_residence || "",
      national_id_number:
        draftPrimary.national_id_number || profile?.national_id_number || "",
      phone: draftPrimary.phone || profile?.phone || "",
      marital_status:
        draftPrimary.marital_status || account?.family_situation || "",
    };

    setPrefilledFields(
      Object.fromEntries(
        Object.entries(mappedProfile).map(([key, value]) => [key, !!value]),
      ),
    );

    setBorrowers((prev) => {
      const primary = prev.find((b) => b.role === "PRIMARY") || prev[0];
      if (!primary) return prev;
      const personal = normalizePersonalInfoForForm(primary.personal_info);
      const nextPersonal = { ...personal };
      let changed = false;
      for (const [key, value] of Object.entries(mappedProfile)) {
        if (!nextPersonal[key] && value) {
          nextPersonal[key] = value;
          changed = true;
        }
      }
      if (!changed) return prev;
      return prev.map((borrower) =>
        borrower === primary
          ? { ...borrower, personal_info: nextPersonal }
          : borrower,
      );
    });
  }, [
    account?.first_name,
    account?.last_name,
    account?.email,
    account?.family_situation,
    profile?.country_of_residence,
    profile?.national_id_number,
    profile?.phone,
    activeDraft?.dossier_id,
  ]);

  React.useEffect(() => {
    setBorrowers((prev) => {
      const primary = prev[0] || createBorrower("PRIMARY");

      const maritalStatus = primary.personal_info?.marital_status || "";

      const needsCoBorrower =
        maritalStatus &&
        !["SINGLE", "DIVORCED", "WIDOWED"].includes(maritalStatus);

      if (needsCoBorrower) {
        return prev[1] ? prev : [primary, createBorrower("CO_APPLICANT")];
      }

      return [primary];
    });
  }, [primaryInfo.marital_status]);

  function updateBorrowerPersonalInfo(index, updater) {
    setBorrowers((prev) => {
      const next = [...prev];
      const borrower =
        next[index] || createBorrower(index === 0 ? "PRIMARY" : "CO_APPLICANT");
      const currentInfo = {
        ...EMPTY_PERSONAL_INFO,
        ...(borrower.personal_info || {}),
      };
      const nextPersonal =
        typeof updater === "function"
          ? updater(currentInfo)
          : { ...currentInfo, ...updater };
      next[index] = {
        ...borrower,
        personal_info: nextPersonal,
      };
      return next;
    });
  }

  function normalizeInput(name, value) {
    if (name === "phone") return value.replace(/[^\d+\s()-]/g, "");

    if (name === "dependents") {
      return value.replace(/[^\d]/g, "");
    }

    if (name === "date_of_birth") return formatDateInput(value);

    return value;
  }

  function handlePrimaryChange(e) {
    const { name, value } = e.target;

    updateBorrowerPersonalInfo(0, { [name]: normalizeInput(name, value) });
  }

  function handleCoBorrowerChange(e) {
    const { name, value } = e.target;

    updateBorrowerPersonalInfo(1, { [name]: normalizeInput(name, value) });
  }

  async function getPlacesLibrary() {
    const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);

    if (!google?.maps?.importLibrary) {
      return null;
    }

    return google.maps.importLibrary("places");
  }

  function handlePrimaryAddressChange(event) {
    const value = event.target.value;

    updateBorrowerPersonalInfo(0, {
      address: value,
    });

    if (addressSearchTimerRef.current) {
      window.clearTimeout(addressSearchTimerRef.current);
    }

    const query = value.trim();

    if (query.length < 3 || !GOOGLE_MAPS_API_KEY) {
      setAddressSuggestions([]);
      setAddressSuggestionsOpen(false);
      setAddressSearching(false);

      return;
    }

    const requestId = ++addressRequestIdRef.current;

    setAddressSearching(true);
    setAddressSuggestionsOpen(true);

    addressSearchTimerRef.current = window.setTimeout(async () => {
      try {
        const placesLibrary = await getPlacesLibrary();

        if (!placesLibrary) {
          setAddressSuggestions([]);
          setAddressSuggestionsOpen(false);

          return;
        }

        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          placesLibrary;

        if (!addressSessionTokenRef.current) {
          addressSessionTokenRef.current = new AutocompleteSessionToken();
        }

        const request = {
          input: query,
          sessionToken: addressSessionTokenRef.current,
          language: (i18n.resolvedLanguage || i18n.language || "en").split(
            "-",
          )[0],
        };

        if (primaryInfo.country_of_residence) {
          const countryCode = primaryInfo.country_of_residence.toLowerCase();

          request.includedRegionCodes = [countryCode];
          request.region = countryCode;
        }

        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (requestId !== addressRequestIdRef.current) {
          return;
        }

        const predictions = (suggestions || [])
          .map((suggestion) => suggestion.placePrediction)
          .filter(Boolean);

        setAddressSuggestions(predictions);
        setAddressSuggestionsOpen(predictions.length > 0);
      } catch (error) {
        console.error("Google address autocomplete failed:", error);

        if (requestId === addressRequestIdRef.current) {
          setAddressSuggestions([]);
          setAddressSuggestionsOpen(false);
        }
      } finally {
        if (requestId === addressRequestIdRef.current) {
          setAddressSearching(false);
        }
      }
    }, 300);
  }

  async function handlePrimaryAddressSelect(prediction) {
    if (!prediction) {
      return;
    }

    try {
      setAddressSearching(true);

      const place = prediction.toPlace();

      await place.fetchFields({
        fields: ["formattedAddress"],
      });

      const selectedAddress =
        place.formattedAddress || prediction.text?.text || "";

      updateBorrowerPersonalInfo(0, {
        address: selectedAddress,
      });

      setAddressSuggestions([]);
      setAddressSuggestionsOpen(false);

      addressSessionTokenRef.current = null;
    } catch (error) {
      console.error("Could not select Google address:", error);
    } finally {
      setAddressSearching(false);
    }
  }

  React.useEffect(() => {
    function handleDocumentClick(event) {
      if (
        addressAutocompleteRef.current &&
        !addressAutocompleteRef.current.contains(event.target)
      ) {
        setAddressSuggestionsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);

      if (addressSearchTimerRef.current) {
        window.clearTimeout(addressSearchTimerRef.current);
      }
    };
  }, []);

  function safeTrim(value) {
    return String(value ?? "").trim();
  }

  function getResidencyStatusValue(personalInfo = {}) {
    if (personalInfo.residency_status === "OTHER") {
      return safeTrim(personalInfo.residency_status_other);
    }

    return personalInfo.residency_status || "";
  }

  function validateBorrower(values, prefix = "primary") {
    const errors = {};

    const isCoBorrower = prefix === "coBorrower";

    if (!safeTrim(values.first_name)) {
      errors[`${prefix}.first_name`] = t("validation.required");
    }

    if (!safeTrim(values.last_name)) {
      errors[`${prefix}.last_name`] = t("validation.required");
    }

    if (!safeTrim(values.date_of_birth)) {
      errors[`${prefix}.date_of_birth`] = t("validation.required");
    } else if (!isValidDateString(values.date_of_birth)) {
      errors[`${prefix}.date_of_birth`] = t("validation.invalidDate");
    }

    if (!safeTrim(values.email)) {
      errors[`${prefix}.email`] = t("validation.required");
    } else if (!isValidEmail(values.email)) {
      errors[`${prefix}.email`] = t("validation.invalidEmail");
    }

    if (!isCoBorrower) {
      if (!values.nationality) {
        errors[`${prefix}.nationality`] = t("validation.required");
      }

      if (!values.country_of_residence) {
        errors[`${prefix}.country_of_residence`] = t("validation.required");
      }

      if (!safeTrim(values.residency_status)) {
        errors[`${prefix}.residency_status`] = t("validation.required");
      }

      if (
        values.residency_status === "OTHER" &&
        !safeTrim(values.residency_status_other)
      ) {
        errors[`${prefix}.residency_status_other`] = t("validation.required");
      }

      if (!safeTrim(values.address)) {
        errors[`${prefix}.address`] = t("validation.required");
      }

      if (!safeTrim(values.phone)) {
        errors[`${prefix}.phone`] = t("validation.required");
      } else if (!hasEnoughPhoneDigits(values.phone)) {
        errors[`${prefix}.phone`] = t("validation.invalidPhone");
      }

      if (!values.marital_status) {
        errors[`${prefix}.marital_status`] = t("validation.required");
      }

      if (String(safeTrim(values.dependents)) === "") {
        errors[`${prefix}.dependents`] = t("validation.required");
      }

      if (!values.preferred_contact_method) {
        errors[`${prefix}.preferred_contact_method`] = t("validation.required");
      }
    }

    return errors;
  }

  React.useEffect(() => {
    setBorrowersDraft((currentDraftBorrowers) => {
      const same =
        JSON.stringify(currentDraftBorrowers || []) ===
        JSON.stringify(borrowers);

      return same ? currentDraftBorrowers : borrowers;
    });
  }, [borrowers, setBorrowersDraft]);

  React.useEffect(() => {
    const primaryErrors = validateBorrower(primaryInfo, "primary");

    const coBorrowerStarted = Object.entries(coBorrowerInfo || {}).some(
      ([key, value]) =>
        key !== "preferred_contact_method" && String(value ?? "").trim() !== "",
    );

    const coErrors =
      hasCoBorrower && coBorrowerStarted
        ? validateBorrower(coBorrowerInfo, "coBorrower")
        : {};

    const marketErrors = {};

    if (!propertyCountry) {
      marketErrors["dossier.country_of_property_purchase"] = t(
        "validation.required",
      );
    }

    if (!marketRegionId) {
      marketErrors["dossier.market_region_id"] = t("validation.required");
    }

    if (
      selectedMarketRegion?.country_code &&
      propertyCountry &&
      selectedMarketRegion.country_code !== propertyCountry
    ) {
      marketErrors["dossier.market_region_id"] = t(
        "market.errors.countryMismatch",

        "The selected market does not belong to the selected country.",
      );
    }

    const errors = {
      ...marketErrors,

      ...primaryErrors,

      ...coErrors,
    };

    setFieldErrors(errors);

    handleStepValidityChange(
      currentStepKey,

      Object.keys(errors).length === 0,

      errors,

      {
        borrowers,

        country_of_property_purchase: propertyCountry,

        market_region_id: marketRegionId ? Number(marketRegionId) : null,

        market_region: selectedMarketRegion,
      },
    );
  }, [
    borrowers,

    primaryInfo,

    coBorrowerInfo,

    hasCoBorrower,

    propertyCountry,

    marketRegionId,

    selectedMarketRegion,

    currentStepKey,

    handleStepValidityChange,

    t,
  ]);

  React.useEffect(() => {
    async function handleStepSubmit() {
      setSubmitError("");

      if (!propertyCountry || !marketRegionId) {
        setSubmitError(
          t(
            "market.errors.required",

            "Select the property country and LEG-UP market before continuing.",
          ),
        );

        return { ok: false };
      }

      // A saved DRAFT must preserve a partially completed co-applicant too.
      // Validation is handled separately; filtering here deleted their
      // information from Django on every draft save.
      const borrowersForBackend = borrowers
        .filter((borrower) => ["PRIMARY", "CO_APPLICANT"].includes(borrower.role))
        .map((borrower) => ({
          ...borrower,
          personal_info: {
            ...(borrower.personal_info || {}),
            residency_status: getResidencyStatusValue(borrower.personal_info),
          },
        }));

      try {
        const accessToken = await getAccessTokenSilently();

        if (activeDossierId) {
          const result = await updateDossierPersonalInfo({
            accessToken,

            dossierId: activeDossierId,

            countryOfPropertyPurchase: propertyCountry,

            borrowers: borrowersForBackend,
          });

          const resolvedMarketRegion =
            result.market_region || selectedMarketRegion || null;

          const resolvedMarketRegionId =
            result.market_region_id ||
            resolvedMarketRegion?.id ||
            (marketRegionId ? Number(marketRegionId) : null);

          updateDraftMetaFields({
            dossier_id: result.dossier_id || activeDossierId,

            dossier_code:
              result.dossier_code || activeDraft.dossier_code || null,

            country_of_property_purchase:
              result.country_of_property_purchase || propertyCountry,

            market_region_id: resolvedMarketRegionId,

            market_region: resolvedMarketRegion,

            dossier_applicant_id: result.primary_dossier_applicant_id || null,

            primary_dossier_applicant_id:
              result.primary_dossier_applicant_id || null,

            co_dossier_applicant_id: result.co_dossier_applicant_id || null,

            borrowers: result.borrowers || borrowers,

            document_requirements: result.document_requirements || [],

            document_requirement_map_by_code:
              result.document_requirement_map_by_code || {},
          });

          return {
            ok: true,

            updatedDraft: {
              ...activeDraft,

              dossier_id: result.dossier_id || activeDossierId,

              dossier_code:
                result.dossier_code || activeDraft.dossier_code || null,

              country_of_property_purchase:
                result.country_of_property_purchase || propertyCountry,

              market_region_id: resolvedMarketRegionId,

              market_region: resolvedMarketRegion,

              borrowers: result.borrowers || borrowers,

              document_requirements: result.document_requirements || [],

              document_requirement_map_by_code:
                result.document_requirement_map_by_code || {},

              primary_dossier_applicant_id:
                result.primary_dossier_applicant_id || null,

              co_dossier_applicant_id: result.co_dossier_applicant_id || null,
            },
          };
        }

        const result = await initializeDossierFromPersonalInfo({
          accessToken,

          countryOfPropertyPurchase: propertyCountry,

          marketRegionId,

          personalInfo: {
            borrowers: borrowersForBackend,
          },
        });

        if (result.already_exists && result.dossier_id) {
          const updated = await updateDossierPersonalInfo({
            accessToken,

            dossierId: result.dossier_id,

            countryOfPropertyPurchase: propertyCountry,

            borrowers: borrowersForBackend,
          });

          const resolvedMarketRegion =
            updated.market_region ||
            result.market_region ||
            selectedMarketRegion ||
            null;

          const resolvedMarketRegionId =
            updated.market_region_id ||
            result.market_region_id ||
            resolvedMarketRegion?.id ||
            Number(marketRegionId);

          updateDraftMetaFields({
            dossier_id: updated.dossier_id || result.dossier_id,

            dossier_code: updated.dossier_code || result.dossier_code || null,

            country_of_property_purchase:
              updated.country_of_property_purchase ||
              result.country_of_property_purchase ||
              propertyCountry,

            market_region_id: resolvedMarketRegionId,

            market_region: resolvedMarketRegion,

            dossier_applicant_id: updated.primary_dossier_applicant_id || null,

            primary_dossier_applicant_id:
              updated.primary_dossier_applicant_id || null,

            co_dossier_applicant_id: updated.co_dossier_applicant_id || null,

            borrowers: updated.borrowers || borrowers,

            document_requirements: updated.document_requirements || [],

            document_requirement_map_by_code:
              updated.document_requirement_map_by_code || {},
          });

          return {
            ok: true,

            updatedDraft: {
              ...activeDraft,

              dossier_id: updated.dossier_id || result.dossier_id,

              dossier_code: updated.dossier_code || result.dossier_code || null,

              country_of_property_purchase:
                updated.country_of_property_purchase ||
                result.country_of_property_purchase ||
                propertyCountry,

              market_region_id: resolvedMarketRegionId,

              market_region: resolvedMarketRegion,

              borrowers: updated.borrowers || borrowers,

              document_requirements: updated.document_requirements || [],

              document_requirement_map_by_code:
                updated.document_requirement_map_by_code || {},

              primary_dossier_applicant_id:
                updated.primary_dossier_applicant_id || null,

              co_dossier_applicant_id: updated.co_dossier_applicant_id || null,
            },
          };
        }

        const resolvedMarketRegion =
          result.market_region || selectedMarketRegion || null;

        const resolvedMarketRegionId =
          result.market_region_id ||
          resolvedMarketRegion?.id ||
          Number(marketRegionId);

        updateDraftMetaFields({
          dossier_id: result.dossier_id,

          dossier_code: result.dossier_code || null,

          country_of_property_purchase:
            result.country_of_property_purchase || propertyCountry,

          market_region_id: resolvedMarketRegionId,

          market_region: resolvedMarketRegion,

          dossier_applicant_id: result.primary_dossier_applicant_id || null,

          primary_dossier_applicant_id:
            result.primary_dossier_applicant_id || null,

          co_dossier_applicant_id: result.co_dossier_applicant_id || null,

          borrowers: result.borrowers || borrowers,

          document_requirements: result.document_requirements || [],

          document_requirement_map_by_code:
            result.document_requirement_map_by_code || {},
        });

        return {
          ok: true,

          updatedDraft: {
            ...activeDraft,

            dossier_id: result.dossier_id,

            dossier_code: result.dossier_code || null,

            country_of_property_purchase:
              result.country_of_property_purchase || propertyCountry,

            market_region_id: resolvedMarketRegionId,

            market_region: resolvedMarketRegion,

            borrowers: result.borrowers || borrowers,

            document_requirements: result.document_requirements || [],

            document_requirement_map_by_code:
              result.document_requirement_map_by_code || {},

            primary_dossier_applicant_id:
              result.primary_dossier_applicant_id || null,

            co_dossier_applicant_id: result.co_dossier_applicant_id || null,
          },
        };
      } catch (err) {
        const message = err.message || "Could not save personal information.";
        setSubmitError(message);
        return { ok: false, error: message };
      }
    }

    registerStepSubmitHandler(currentStepKey, handleStepSubmit);

    return () => {
      registerStepSubmitHandler(currentStepKey, null);
    };
  }, [
    activeDossierId,

    activeDraft,

    borrowers,

    coBorrowerInfo,

    currentStepKey,

    getAccessTokenSilently,

    marketRegionId,

    propertyCountry,

    registerStepSubmitHandler,

    selectedMarketRegion,

    t,

    updateDraftMetaFields,
  ]);

  function renderLabel(
    label,

    fieldName,

    isPrefilled = false,

    mode = "primary",
  ) {
    const required = isRequiredField(fieldName, mode);

    return (
      <label className="hb-field-label-row">
        <span>
          {label}

          {required ? <span className="hb-required-asterisk"> *</span> : null}
        </span>

        {isPrefilled && (
          <span className="hb-prefilled-tag">{t("common.prefilled")}</span>
        )}

        <span className="hb-tooltip-wrap" tabIndex={0}>
          <Info size={14} strokeWidth={2.2} />

          <span className="hb-tooltip">{FIELD_TOOLTIPS[fieldName]}</span>
        </span>
      </label>
    );
  }

  function renderError(key) {
    const message = fieldErrors[key];
    // Asterisk communicates missing input. Format errors remain inline.
    if (!message || message === t("validation.required")) return null;
    return <span className="hb-field-error" role="alert">{message}</span>;
  }

  return (
    <div className="hb-step-page">
      <div className="hb-step-page__header">
        <h1 className="hb-step-page__title">{t("title")}</h1>

        <button
          type="button"
          className="hb-app-flow__save-btn"
          onClick={openSaveModal}
        >
          <Save size={16} />

          <span>{t("buttons.save")}</span>
        </button>
      </div>

      <div className="hb-step-page__panel">
        <div className="hb-step-page__intro-row">
          <p className="hb-step-page__intro">{t("intro")}</p>
        </div>

        {submitError ? <div className="hb-doc-alert">{submitError}</div> : null}

        <section className="hb-step-card hb-step-card--market">
          <h2 className="hb-step-card__title">
            {t("market.title", "Property market")}
          </h2>

          <p className="hb-step-page__intro">
            {t(
              "market.description",

              "Select where the property is or will be purchased. LEG-UP will use this market to determine the participating lenders, available OSS organisations and market-specific configuration.",
            )}
          </p>

          <div className="hb-step-form-grid">
            <div className="hb-field hb-field--with-tooltip">
              <label className="hb-field-label-row">
                <span>
                  {t(
                    "fields.country_of_property_purchase",

                    "Country of property purchase",
                  )}

                  <span className="hb-required-asterisk"> *</span>
                </span>
              </label>

              <select
                value={propertyCountry}
                onChange={(event) => {
                  const nextCountry = event.target.value;

                  setPropertyCountry(nextCountry);

                  setSubmitError("");

                  if (!activeDossierId) {
                    setMarketRegionId("");
                  }
                }}
                disabled={Boolean(activeDossierId) && Boolean(marketRegionId)}
              >
                <option value="">
                  {t("placeholders.selectCountry", "Select country")}
                </option>

                {PROPERTY_PURCHASE_COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {t(`common:countries.${country.code}`, {
                      defaultValue: country.label,
                    })}
                  </option>
                ))}
              </select>

              {renderError("dossier.country_of_property_purchase")}
            </div>

            <div className="hb-field hb-field--with-tooltip">
              <label className="hb-field-label-row">
                <span>
                  {t("fields.market_region", "LEG-UP market region")}

                  <span className="hb-required-asterisk"> *</span>
                </span>
              </label>

              <select
                value={marketRegionId}
                onChange={(event) => {
                  setMarketRegionId(event.target.value);

                  setSubmitError("");
                }}
                disabled={
                  (Boolean(activeDossierId) && Boolean(marketRegionId)) ||
                  !propertyCountry ||
                  loadingMarketRegions
                }
              >
                <option value="">
                  {loadingMarketRegions
                    ? t("market.loading", "Loading markets...")
                    : t("market.select", "Select market region")}
                </option>

                {marketRegions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>

              {renderError("dossier.market_region_id")}

              {!loadingMarketRegions &&
              propertyCountry &&
              marketRegions.length === 0 ? (
                <span className="hb-field-error">
                  {t(
                    "market.none",

                    "LEG-UP is not currently active in the selected country.",
                  )}
                </span>
              ) : null}

              {!activeDossierId &&
              marketRegions.length === 1 &&
              selectedMarketRegion ? (
                <span className="hb-field-hint">
                  {t(
                    "market.autoSelected",

                    "The only active LEG-UP market for this country has been selected automatically.",
                  )}
                </span>
              ) : null}

              {activeDossierId ? (
                <span className="hb-field-hint">
                  {t(
                    "market.locked",

                    "The operational market is fixed after the dossier is created.",
                  )}
                </span>
              ) : null}
            </div>
          </div>
        </section>

        <div className="hb-step-page__grid">
          <div className="hb-step-card">
            <h2 className="hb-step-card__title">{t("sections.identity")}</h2>

            <div className="hb-step-form-grid">
              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(
                  t("fields.first_name"),

                  "first_name",

                  prefilledFields.first_name,
                )}

                <input
                  name="first_name"
                  value={primaryInfo.first_name}
                  onChange={handlePrimaryChange}
                />

                {renderError("primary.first_name")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(
                  t("fields.last_name"),

                  "last_name",

                  prefilledFields.last_name,
                )}

                <input
                  name="last_name"
                  value={primaryInfo.last_name}
                  onChange={handlePrimaryChange}
                />

                {renderError("primary.last_name")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.date_of_birth"), "date_of_birth")}

                <input
                  name="date_of_birth"
                  value={primaryInfo.date_of_birth}
                  onChange={handlePrimaryChange}
                  placeholder={t("placeholders.date_of_birth")}
                  inputMode="numeric"
                  maxLength={10}
                />

                {renderError("primary.date_of_birth")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.nationality"), "nationality")}

                <select
                  name="nationality"
                  value={primaryInfo.nationality}
                  onChange={handlePrimaryChange}
                >
                  <option value="">
                    {t("placeholders.selectNationality")}
                  </option>

                  {EUROPEAN_COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label}
                    </option>
                  ))}
                </select>

                {renderError("primary.nationality")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(
                  t("fields.country_of_residence"),

                  "country_of_residence",

                  prefilledFields.country_of_residence,
                )}

                <select
                  name="country_of_residence"
                  value={primaryInfo.country_of_residence}
                  onChange={handlePrimaryChange}
                >
                  <option value="">{t("placeholders.selectCountry")}</option>

                  {EUROPEAN_COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label}
                    </option>
                  ))}
                </select>

                {renderError("primary.country_of_residence")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.residency_status"), "residency_status")}

                <select
                  name="residency_status"
                  value={primaryInfo.residency_status}
                  onChange={handlePrimaryChange}
                >
                  <option value="">
                    {t("placeholders.selectResidencyStatus")}
                  </option>

                  <option value="CITIZEN">
                    {t("options.residency.citizen")}
                  </option>

                  <option value="PERMANENT_RESIDENT">
                    {t("options.residency.permanentResident")}
                  </option>

                  <option value="LONG_TERM_RESIDENT_EU">
                    {t("options.residency.longTermResidentEu")}
                  </option>

                  <option value="EU_BLUE_CARD">
                    {t("options.residency.euBlueCard")}
                  </option>

                  <option value="FAMILY_MEMBER_EU_EEA">
                    {t("options.residency.familyMemberEu")}
                  </option>

                  <option value="TEMPORARY_RESIDENCE_PERMIT">
                    {t("options.residency.temporaryResidencePermit")}
                  </option>

                  <option value="OTHER">{t("options.residency.other")}</option>
                </select>

                {renderError("primary.residency_status")}

                {primaryInfo.residency_status === "OTHER" && (
                  <>
                    <input
                      name="residency_status_other"
                      value={primaryInfo.residency_status_other || ""}
                      onChange={handlePrimaryChange}
                      placeholder={t("placeholders.specifyResidencyStatus")}
                    />

                    {renderError("primary.residency_status_other")}
                  </>
                )}
              </div>

              <div className="hb-field hb-field--full hb-field--with-tooltip">
                {renderLabel(
                  t("fields.national_id_number"),

                  "national_id_number",

                  prefilledFields.national_id_number,
                )}

                <input
                  name="national_id_number"
                  value={primaryInfo.national_id_number}
                  onChange={handlePrimaryChange}
                />

                {renderError("primary.national_id_number")}
              </div>

              <div className="hb-field hb-field--full hb-field--with-tooltip">
                {renderLabel(t("fields.address"), "address")}

                <div
                  className="hb-address-autocomplete"
                  ref={addressAutocompleteRef}
                >
                  <input
                    name="address"
                    value={primaryInfo.address}
                    onChange={handlePrimaryAddressChange}
                    onFocus={() => {
                      if (addressSuggestions.length > 0) {
                        setAddressSuggestionsOpen(true);
                      }
                    }}
                    placeholder={t(
                      "placeholders.address",
                      "Start typing your address...",
                    )}
                    autoComplete="off"
                  />

                  {addressSearching ? (
                    <span className="hb-address-autocomplete__loading">
                      {t("address.searching", "Searching...")}
                    </span>
                  ) : null}

                  {addressSuggestionsOpen && addressSuggestions.length > 0 ? (
                    <div className="hb-address-autocomplete__suggestions">
                      {addressSuggestions.map((prediction, index) => (
                        <button
                          key={
                            prediction.placeId || prediction.text?.text || index
                          }
                          type="button"
                          className="hb-address-autocomplete__suggestion"
                          onMouseDown={(event) => {
                            event.preventDefault();
                          }}
                          onClick={() => handlePrimaryAddressSelect(prediction)}
                        >
                          {prediction.text?.text ||
                            prediction.mainText?.text ||
                            ""}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                {renderError("primary.address")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.email"), "email", prefilledFields.email)}

                <input
                  name="email"
                  type="email"
                  value={primaryInfo.email}
                  onChange={handlePrimaryChange}
                />

                {renderError("primary.email")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.phone"), "phone", prefilledFields.phone)}

                <input
                  name="phone"
                  value={primaryInfo.phone}
                  onChange={handlePrimaryChange}
                />

                {renderError("primary.phone")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(
                  t("fields.marital_status"),

                  "marital_status",

                  prefilledFields.marital_status,
                )}

                <select
                  name="marital_status"
                  value={primaryInfo.marital_status}
                  onChange={handlePrimaryChange}
                >
                  <option value="">{t("placeholders.selectStatus")}</option>

                  <option value="SINGLE">{t("options.marital.single")}</option>

                  <option value="MARRIED">
                    {t("options.marital.married")}
                  </option>

                  <option value="PARTNERSHIP">
                    {t("options.marital.partnership")}
                  </option>

                  <option value="DIVORCED">
                    {t("options.marital.divorced")}
                  </option>

                  <option value="WIDOWED">
                    {t("options.marital.widowed")}
                  </option>
                </select>

                {renderError("primary.marital_status")}
              </div>

              <div className="hb-field hb-field--with-tooltip">
                {renderLabel(t("fields.dependents"), "dependents")}

                <input
                  name="dependents"
                  value={primaryInfo.dependents}
                  onChange={handlePrimaryChange}
                />

                {renderError("primary.dependents")}
              </div>
            </div>
          </div>

          <div className="hb-step-page__side-column">
            <div
              className={`hb-step-card ${!hasCoBorrower ? "is-disabled" : ""}`}
            >
              <h2 className="hb-step-card__title">
                {t("sections.coBorrower")}
              </h2>

              <div className="hb-step-form-grid">
                <div className="hb-field hb-field--with-tooltip">
                  {renderLabel(
                    t("fields.first_name"),

                    "first_name",

                    false,

                    "coBorrower",
                  )}

                  <input
                    name="first_name"
                    value={coBorrowerInfo.first_name}
                    onChange={handleCoBorrowerChange}
                    disabled={!hasCoBorrower}
                  />

                  {hasCoBorrower && renderError("coBorrower.first_name")}
                </div>

                <div className="hb-field hb-field--with-tooltip">
                  {renderLabel(
                    t("fields.date_of_birth"),

                    "date_of_birth",

                    false,

                    "coBorrower",
                  )}

                  <input
                    name="date_of_birth"
                    value={coBorrowerInfo.date_of_birth}
                    onChange={handleCoBorrowerChange}
                    disabled={!hasCoBorrower}
                    placeholder={t("placeholders.date_of_birth")}
                    inputMode="numeric"
                    maxLength={10}
                  />

                  {hasCoBorrower && renderError("coBorrower.date_of_birth")}
                </div>

                <div className="hb-field hb-field--with-tooltip">
                  {renderLabel(
                    t("fields.last_name"),

                    "last_name",

                    false,

                    "coBorrower",
                  )}

                  <input
                    name="last_name"
                    value={coBorrowerInfo.last_name}
                    onChange={handleCoBorrowerChange}
                    disabled={!hasCoBorrower}
                  />

                  {hasCoBorrower && renderError("coBorrower.last_name")}
                </div>

                <div className="hb-field hb-field--with-tooltip">
                  {renderLabel(t("fields.email"), "email", false, "coBorrower")}

                  <input
                    name="email"
                    type="email"
                    value={coBorrowerInfo.email}
                    onChange={handleCoBorrowerChange}
                    disabled={!hasCoBorrower}
                  />

                  {hasCoBorrower && renderError("coBorrower.email")}
                </div>
              </div>
            </div>

            <div className="hb-step-card">
              <h2 className="hb-step-card__title">
                {t("sections.communicationNotes")}
              </h2>

              <div className="hb-step-form-grid">
                <div className="hb-field hb-field--full hb-field--with-tooltip">
                  {renderLabel(
                    t("fields.preferred_contact_method"),

                    "preferred_contact_method",
                  )}

                  <select
                    name="preferred_contact_method"
                    value={primaryInfo.preferred_contact_method}
                    onChange={handlePrimaryChange}
                  >
                    <option value="EMAIL">{t("options.contact.email")}</option>

                    <option value="PHONE">{t("options.contact.phone")}</option>
                  </select>

                  {renderError("primary.preferred_contact_method")}
                </div>

                <div className="hb-field hb-field--full hb-field--with-tooltip">
                  {renderLabel(t("fields.notes"), "notes")}

                  <textarea
                    name="notes"
                    value={primaryInfo.notes}
                    onChange={handlePrimaryChange}
                    rows={4}
                    placeholder={t("placeholders.notes")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
